import { DONT_SHOW } from 'src/views/dpxChat/components/constants/modalTemplates';
import { CapitalizeNames as capitalize } from 'src/utils/capitalize';
import { TFormatMessageOptions } from 'src/components/Lead/WhatsApp/utils/types';

/**
 * Array of keys that should not be capitalized when processing template variables
 */
const KEYS_THAT_SHOULD_NOT_BE_CAPITALIZED = [
    'makeModel',
    'storePhone',
    'date',
    'storeAddressLink',
    'useremail',
    'vehiclemodel',
    'model',
];

/**
 * Validates and sanitizes text by replacing undefined values with DONT_SHOW constant
 *
 * @param {string} text - The text to validate
 * @returns {string} - The original text if valid, or DONT_SHOW if undefined or contains 'undefined'
 *
 * @example
 * validateAndSanitizeText('John Doe') // returns 'John Doe'
 * validateAndSanitizeText('undefined') // returns 'DONT_SHOW'
 * validateAndSanitizeText(null) // returns 'DONT_SHOW'
 */
export const validateAndSanitizeText = (
    text: string | undefined | null
): string => {
    if (!text || text.toLowerCase().includes('undefined')) {
        return DONT_SHOW;
    }
    return text;
};

/**
 * Validates, capitalizes, and sanitizes text for template variables
 *
 * @param {string} text - The text to process
 * @returns {string} - The processed text (capitalized and sanitized)
 *
 * @example
 * processTextWithCapitalization('john doe') // returns 'John Doe'
 * processTextWithCapitalization('undefined') // returns 'DONT_SHOW'
 */
export const processTextWithCapitalization = (text: string): string => {
    return validateAndSanitizeText(capitalize(text));
};

/**
 * Validates and sanitizes text without applying capitalization
 *
 * @param {string} text - The text to process
 * @returns {string} - The processed text (sanitized but not capitalized)
 *
 * @example
 * processTextWithoutCapitalization('john doe') // returns 'john doe'
 * processTextWithoutCapitalization('undefined') // returns 'DONT_SHOW'
 */
export const processTextWithoutCapitalization = (text: string): string => {
    return validateAndSanitizeText(text);
};

/**
 * Processes all template variables by applying appropriate validation and formatting
 * Applies capitalization to most fields, but skips it for specific keys like makeModel and storePhone
 *
 * @param {TFormatMessageOptions} templateVariables - Object containing all template variables
 * @returns {TFormatMessageOptions} - Object with all variables processed and sanitized
 *
 * @example
 * const variables = { store: 'my store', makeModel: 'Toyota Camry', storePhone: '123-456-7890' };
 * processAllTemplateVariables(variables);
 * // Returns: { storename: 'My Store', makeModel: 'Toyota Camry', storePhone: '123-456-7890' }
 */
export const processAllTemplateVariables = (
    templateVariables: TFormatMessageOptions
): TFormatMessageOptions => {
    const response = Object.fromEntries(
        Object.entries(templateVariables).map(([key, value]) => [
            key,
            KEYS_THAT_SHOULD_NOT_BE_CAPITALIZED.includes(key)
                ? processTextWithoutCapitalization(value)
                : processTextWithCapitalization(value),
        ])
    );

    return response;
};
